import { apiService } from './ApiService';
import { ApiResponse } from '../interfaces/responses/ApiResponse';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const baseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

export interface IFxRateResponse {
  from: string;
  to: string;
  rate: number;
}

// Shares one in-flight request across every card/component asking for the
// same currency pair on the same page (a search results page can render
// dozens of HeavyDutyMachineryCards at once) -- the backend's own
// FxRateController already caches for 1h server-side, this just avoids
// firing off a burst of otherwise-identical requests for it to de-dupe.
const inFlightRequests = new Map<string, Promise<number>>();

/**
 * Display-only conversion (e.g. listing cards, the detail page) -- never
 * used to charge anything. A real booking's charge always freezes its own
 * rate server-side at CreateBooking time (Booking.ExchangeRate); this is
 * just "≈ $X" while the guest is still browsing. See FxRateController's
 * own doc comment.
 */
export const getFxRate = async (from: string, to: string): Promise<number> => {
  const key = `${from.toUpperCase()}:${to.toUpperCase()}`;
  const cached = inFlightRequests.get(key);
  if (cached) return cached;

  const request = (async () => {
    const response = await apiService<ApiResponse<IFxRateResponse>>({
      url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_FX_RATE(from, to),
      method: HTTP_METHODS.GET,
      baseUrl: baseUrl,
    });

    if (response.success && response.data.success) return response.data.data.rate;
    throw new Error(response.error || response.data.error || 'Failed to fetch FX rate');
  })();

  inFlightRequests.set(key, request);
  try {
    return await request;
  } finally {
    // Only de-dupes requests that are actually in flight together -- once
    // this settles, a later call gets a fresh request (and the backend's
    // own 1h cache), not a stale resolved value replayed forever.
    inFlightRequests.delete(key);
  }
};
